import useInvoiceTaxAuthorityConfigs from 'Hooks/useInvoiceTaxAuthorityConfigs';
import useDrawerStack from 'Hooks/useDrawerStack';
import useCountries from 'Hooks/useCountries';
import { deleteInvoiceTaxAuthorityConfig } from 'Services/invoices';
import { useQueryClient } from '@tanstack/react-query';
import { DRAWER_STACK } from 'Utils/constants';
import Button from 'Components/Atoms/Button/Button';
import IconButton from 'Components/Atoms/IconButton/IconButton';
import OutlineEdit from 'Components/Atoms/OutlineIcons/OutlineEdit';
import ActionsMenu from 'Components/Molecules/ActionsMenu/ActionsMenu';
import Loading from 'Components/Atoms/Loading/Loading';
import { useTranslation } from 'react-i18next';
import { cn } from 'Utils/cn';

export default function InvoicesInvoicingTaxAuthorityConfigDrawer() {
    const { taxAuthorityConfigs, isLoading } = useInvoiceTaxAuthorityConfigs();
    const { open } = useDrawerStack();
    const { countries } = useCountries();
    const queryClient = useQueryClient();
    const { t } = useTranslation('invoices');
    const { t: tContacts } = useTranslation('contacts');

    const getCountryName = (code) => {
        if (!code) return t('tax-authority-config-all-option');

        const country = (countries ?? []).find((c) => c.code === code);
        return country?.name ?? code;
    };

    const getSubtypeLabel = (subtype) => {
        if (!subtype) return t('tax-authority-config-all-option');
        const map = {
            standard: t('invoice-standard'),
            standard_corrective: t('invoice-standard-corrective'),
            simplified: t('invoice-simplified'),
            simplified_corrective: t('invoice-simplified-corrective'),
            quote: t('invoice-quote'),
            proforma: t('invoice-proforma'),
            delivery_note: t('invoice-delivery-note'),
        };
        return map[subtype] ?? subtype;
    };

    const getContactTypeLabel = (contactType) => {
        if (!contactType) return t('tax-authority-config-all-option');
        const map = {
            company: tContacts('company'),
            freelancer: tContacts('freelancer'),
            individual: tContacts('individual'),
        };
        return map[contactType] ?? contactType;
    };

    const handleDelete = (config) => {
        open(DRAWER_STACK.CONFIRMATION_DIALOG, {
            title: t('tax-authority-config-delete-title'),
            description: t('tax-authority-config-delete-description'),
            onSubmit: async () => {
                await deleteInvoiceTaxAuthorityConfig(config.id);
                await queryClient.invalidateQueries({ queryKey: ['invoices', 'tax-authority-configs'] });
            },
        });
    };

    if (isLoading) {
        return (
            <div className="flex justify-center py-12">
                <Loading />
            </div>
        );
    }

    if (!taxAuthorityConfigs?.length) {
        return (
            <div className="flex flex-col items-center justify-center gap-3 text-center py-12">
                <p className="text-sm text-gray-500 mb-3 max-w-[380px]">{t('tax-authority-config-empty')}</p>
                <Button
                    variant="primary"
                    onClick={() =>
                        open(DRAWER_STACK.INVOICE_TAX_AUTHORITY_CONFIG_CREATE, {
                            title: t('tax-authority-config-create-title'),
                        })
                    }
                >
                    {t('tax-authority-config-new-button')}
                </Button>
            </div>
        );
    }

    return (
        <div className="py-4">
            <div className="mb-3">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-2xs text-gray-400 uppercase tracking-wide">
                            <th className="text-left py-1 font-medium pl-1">{t('tax-authority-config-col-country')}</th>
                            <th className="text-left py-1 font-medium">{t('tax-authority-config-col-subtype')}</th>
                            <th className="text-left py-1 font-medium">{t('tax-authority-config-col-contact-type')}</th>
                            <th className="py-1 w-10" />
                        </tr>
                    </thead>
                    <tbody>
                        {taxAuthorityConfigs.map((config) => (
                            <tr key={config.id} className="group border-t border-gray-100">
                                <td
                                    className={cn(
                                        'text-gray-500 text-sm py-1.5 group-hover:bg-gray-50 pl-1 rounded-l-md',
                                    )}
                                >
                                    {getCountryName(config.country)}
                                </td>
                                <td className="text-gray-500 text-sm py-1.5 group-hover:bg-gray-50">
                                    {getSubtypeLabel(config.invoice_subtype)}
                                </td>
                                <td className="text-gray-500 text-sm py-1.5 group-hover:bg-gray-50">
                                    {getContactTypeLabel(config.contact_type)}
                                </td>
                                <td className="text-center py-1.5 group-hover:bg-gray-50 rounded-r-md">
                                    <div className="flex items-center justify-end gap-0.5">
                                        <IconButton
                                            variant="ghost"
                                            size="small"
                                            icon={<OutlineEdit color="currentColor" />}
                                            tooltip={t('tax-authority-config-edit-button')}
                                            onClick={() =>
                                                open(DRAWER_STACK.INVOICE_TAX_AUTHORITY_CONFIG_EDIT, {
                                                    title: t('tax-authority-config-edit-title'),
                                                    configId: config.id,
                                                })
                                            }
                                        />
                                        <ActionsMenu
                                            className="px-1 h-8"
                                            disablePortal
                                            actions={[
                                                {
                                                    label: t('tax-authority-config-delete-button'),
                                                    onClick: () => handleDelete(config),
                                                    variant: 'danger',
                                                },
                                            ]}
                                        />
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="flex items-center justify-between">
                <Button
                    variant="primary"
                    onClick={() =>
                        open(DRAWER_STACK.INVOICE_TAX_AUTHORITY_CONFIG_CREATE, {
                            title: t('tax-authority-config-create-title'),
                        })
                    }
                >
                    {t('tax-authority-config-new-button')}
                </Button>
            </div>
        </div>
    );
}
